import { h } from 'preact';
import { useState } from 'preact/hooks';
import useFormStore from '../../store/useFormStore';

export default function PromotionsCodeSearch() {
  const currentFranchise = useFormStore((state) => state.currentFranchise);
  const manuallyEnteredPromoCodes = useFormStore((state) => state.manuallyEnteredPromoCodes);
  const addManualPromoCode = useFormStore((state) => state.addManualPromoCode);

  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const handleApplyPromoCode = async () => {
    setPromoError('');
    setPromoSuccess('');

    if (!promoCode.trim()) {
      setPromoError('Please enter a promo code');
      return;
    }

    setIsSearching(true);

    try {
      // Find promo in franchise promotions (case insensitive)
      const promo = currentFranchise?.promotions?.find(
        (p) => p.promo_code.toLowerCase() === promoCode.trim().toLowerCase()
      );

      if (!promo) {
        setPromoError('Invalid promo code');
        setIsSearching(false);
        return;
      }

      // Check if already manually entered
      if (manuallyEnteredPromoCodes.includes(promo.promo_code)) {
        setPromoError('This promo code has already been entered');
        setIsSearching(false);
        return;
      }

      // Add to manually entered codes - this will trigger validation through cart
      addManualPromoCode(promo.promo_code);

      // Wait a bit for cart validation to complete
      await new Promise(resolve => setTimeout(resolve, 100));

      // Check if it was actually applied (validation passed)
      const state = useFormStore.getState();

      // For promos with requirement: false, they're always valid (can be applied anytime)
      if (promo.requirement === false) {
        // Check if cart is empty
        const cartItems = state.selectedServicesConfiguration;
        const hasItems = cartItems && Object.keys(cartItems).length > 0;

        if (hasItems && state.appliedPromotions.includes(promo.promo_code)) {
          setPromoSuccess(`Applied: ${promo.title}`);
        } else if (!hasItems) {
          setPromoSuccess(`${promo.title} saved! Will be applied when you add services.`);
        } else {
          setPromoSuccess(`Applied: ${promo.title}`);
        }
        setPromoCode('');
      } else if (state.appliedPromotions.includes(promo.promo_code)) {
        setPromoSuccess(`Applied: ${promo.title}`);
        setPromoCode('');
      } else {
        setPromoError(`Promo code "${promo.promo_code}" is not valid for your current cart`);
        // Remove from manually entered since it didn't apply
        state.removeManualPromoCode(promo.promo_code);
      }
    } catch (error) {
      setPromoError('Error applying promo code');
    } finally {
      setIsSearching(false);
    }
  };

  const handlePromoInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleApplyPromoCode();
    }
  };

  return (
    <div className="mb-4">
      <label
        htmlFor="promo-code-input"
        className="block text-lg font-semibold text-gray-900 mb-3"
      >
        Other Promo Code
      </label>
      <div className="flex gap-0">
        <input
          id="promo-code-input"
          type="text"
          value={promoCode}
          onInput={(e) => setPromoCode(e.target.value)}
          onKeyDown={handlePromoInputKeyDown}
          placeholder="Enter promo code"
          className="flex-1 px-4 py-2 border-2 mb-0 border-gray-300 focus:border-coit-red focus:outline-none transition-colors"
          disabled={isSearching}
        />
        <button
          onClick={handleApplyPromoCode}
          disabled={isSearching}
          className="bg-coit-red py-2 px-2 cursor-pointer text-white hover:bg-coit-red-darker text-base"
        >
          {isSearching ? 'APPLYING...' : 'APPLY'}
        </button>
      </div>

      {/* Error Message */}
      {promoError && (
        <div className="mt-2 text-sm text-red-600">
          {promoError}
        </div>
      )}

      {/* Success Message */}
      {promoSuccess && (
        <div className="mt-2 text-sm text-green-600 font-medium">
          {promoSuccess}
        </div>
      )}
    </div>
  );
}
